/**
 *  Created by piotr@revisual.io on 15/08/2025
 */
import { orderQuotas } from "../../helpers/subscription";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { hasSubscription } from "../../helpers/organization";
import { QUOTAS } from "../../../../consts";
import PlanComparisonBar from "./PlanComparisonBar";
import { useMemo } from "react";
import classNames from "classnames";

const LabelMapping = {
  [QUOTAS.widgets]: (q) => `Published Widget${q !== 1 ? "s" : ""}`,
  [QUOTAS.team]: (q) => `Team Member${q !== 1 ? "s" : ""}`,
  [QUOTAS.domains]: (q) => `Website Domain${q !== 1 ? "s" : ""}`,
  [QUOTAS.integrations]: (q) => `Integration${q !== 1 ? "s" : ""}`,
  [QUOTAS.events]: (q) => "Synced Events / month",
  [QUOTAS.eventsMonthsAhead]: (q) =>
    `Month${q !== 1 ? "s" : ""} of Future Events Pulled`,
  [QUOTAS.views]: (q) => "Widget Views / month",
};

const ShowBarFor = [
  QUOTAS.widgets,
  QUOTAS.integrations,
  QUOTAS.domains,
  QUOTAS.team,
];

const SimpleUsage = ({ quotaKey, quota }) => {
  const numberFormatter = Intl.NumberFormat(window.navigator.locale || "en-US");

  let suffix = "";

  switch (quotaKey) {
    case QUOTAS.views:
      suffix = "widget views";
      break;
    case QUOTAS.events:
      suffix = "events";
      break;
    case QUOTAS.eventsMonthsAhead:
      suffix = "months";
      break;
  }

  return (
    <>
      {numberFormatter.format(quota)} {suffix}
    </>
  );
};

const UsageLabel = ({ quotaKey, usage, newLimit }) => {
  let content = null;
  switch (quotaKey) {
    case QUOTAS.eventsMonthsAhead:
    case QUOTAS.views:
    case QUOTAS.events:
      content = (
        <span
          className={classNames(
            "d-flex align-items-center gap-xxs text-warning"
          )}
        >
          <i className={"material-icons"}>warning</i>Will be reduced
        </span>
      );

      break;

    default:
      const overLimit = usage > newLimit && newLimit !== -1;
      content = (
        <span
          className={classNames("d-flex align-items-center gap-xxs", {
            "text-success": usage <= newLimit,
            "text-danger": overLimit,
          })}
        >
          <i className={"material-icons icon-sm"}>
            {overLimit ? "warning" : "check_circle"}
          </i>
          {overLimit ? "Over " : "Within "} new limit
        </span>
      );
  }

  return content;
};

/**
 *
 * @param {number} quota
 * @param {number} usage
 * @param {string} icon
 * @param {string} quotaKey
 * @param {number} newQuota
 * @returns {JSX.Element}
 * @constructor
 */
export const PricingComparisonTableItem = ({
  quota,
  usage,
  icon,
  quotaKey,
  newQuota,
}) => {
  const quotaLabel = LabelMapping[quotaKey](quota);

  const showUsageBar = useMemo(
    () =>
      ![QUOTAS.events, QUOTAS.views, QUOTAS.eventsMonthsAhead].includes(
        quotaKey
      ),
    [quotaKey]
  );

  const infoLabel = useMemo(() => {
    /*if (quotaKey === QUOTAS.views) {
      return (
        <div className="text-secondary">
          Not currently tracked. Usage not displayed.
        </div>
      );
    } else if (quotaKey === QUOTAS.events) {
      return (
        <div className="text-secondary">
          Not currently tracked. Unlimited for now.
        </div>
      );
    } else if (quotaKey === QUOTAS.eventsMonthsAhead) {
      return (
        <div className="text-secondary">
          Hard limit. Customization not currently available.
        </div>
      );
    }*/

    if (quota === -1) {
      return (
        <div className="text-secondary">
          Your plan includes unlimited {quotaLabel.toLowerCase()}.
        </div>
      );
    }

    return null;
  }, []);

  const usageBar = showUsageBar ? (
    <PlanComparisonBar limit={quota} newLimit={newQuota} usage={usage} />
  ) : null;

  const simpleSummary = !showUsageBar ? (
    <div>
      <div className={"d-flex gap-xxs"}>
        <div>Current Plan: </div>
        <div>
          <SimpleUsage quotaKey={quotaKey} quota={quota} />
        </div>
      </div>
      <div className={"d-flex gap-xxs"}>
        <div>New Plan: </div>
        <div>
          <SimpleUsage quotaKey={quotaKey} quota={newQuota} />
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="plan-comparison-table-item">
      <div className="plan-comparison-table-item-header">
        <strong>{quotaLabel}</strong>
        <UsageLabel quotaKey={quotaKey} usage={usage} newLimit={newQuota} />
      </div>
      {infoLabel}
      {usageBar}
      {simpleSummary}
    </div>
  );
};

export default PricingComparisonTableItem;
